import { createSlice } from '@reduxjs/toolkit'
import {
  getEncounters,
  getFacilities,
  getFilterOptions,
  getPermissions,
  refreshEncounters,
} from './encountersThunk'
import { format, subDays } from 'date-fns'
import { getPOSDisplay } from '../../components/common/shared/sharedFacilityLogic'

const initialCurrentFiltersState = {
  encounter_statuses: [],
  note_statuses: [],
  facilities: [],
  providers: [],
  procedure_codes: [],
  service_date_from_lbound: format(subDays(new Date(), 30), 'yyyy-MM-dd'),
}

/** This handles the Redux aspects of creating a slice for the encounters page using Redux Toolkit. See: https://redux-toolkit.js.org/api/createslice
 * @param object - An object literal that has the keys name, initialState and reducers, etc.
 * @return object - Exports both the resulting actions and reducer objects on encountersSlice
 */
export const encountersSlice = createSlice({
  name: 'encounters',
  initialState: {
    isLoading: false,
    currentScope: 'provider', // can be either 'practice' or 'provider'
    currentProviders: [], // will be an array of provider ID's
    currentPage: 1, // used for pagination
    currentPageLimit: 50, // default value is 50... possible values: 25, 50, 75, 100
    permissions: [],
    encounter_statuses: [],
    note_statuses: [],
    facilities: [],
    allFacilities: [],
    userProviders: [],
    practiceProviders: [],
    procedure_codes: [],
    currentFilters: { ...initialCurrentFiltersState },
    currentSearch: '',
    currentSort: {
      service_date_from: -1,
    },
    currentSortKey: 'service_date_from',
    currentSortDirection: -1,
    currentSelectedItems: [],
    pagination: {},
    encounters: [],
  },
  reducers: {
    setEncountersisLoading: (state, action) => {
      state.isLoading = action.payload
    },
    switchScope: (state, action) => {
      state.currentScope = action.payload
    },
    setCurrentPage: (state, action) => {
      state.currentPage = action.payload
    },
    setPageLimit: (state, action) => {
      state.currentPageLimit = action.payload
    },
    setCurrentFilter: (state, action) => {
      state.currentFilters[action.payload.key] = action.payload.value
    },
    setCurrentFilters: (state, action) => {
      state.currentFilters = action.payload
    },
    clearCurrentFilters: (state, action) => {
      state.currentFilters = { ...initialCurrentFiltersState }
    },
    setCurrentSearch: (state, action) => {
      state.currentSearch = action.payload
    },
    setCurrentSort: (state, action) => {
      state.currentSort = action.payload
    },
    setCurrentSortKey: (state, action) => {
      state.currentSortKey = action.payload
    },
    setCurrentSortDirection: (state, action) => {
      state.currentSortDirection = action.payload
    },
    setDashboardInProgressState: (state, action) => {
      const isAdmin = action.payload
      state.currentScope = isAdmin ? 'practice' : 'provider'
      encountersSlice.caseReducers.clearCurrentFilters(state)
      encountersSlice.caseReducers.setCurrentFilter(state, {
        payload: { key: 'encounter_statuses', value: ['in-progress'] },
      })
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getEncounters.pending, (state, action) => {
        // don't reset encounters until we have them
        // state.encounters = []
        state.isLoading = true
      })
      .addCase(getEncounters.fulfilled, (state, action) => {
        state.encounters = action.payload.encounters
        state.pagination = action.payload.pagination
        state.isLoading = false
      })
      .addCase(getEncounters.rejected, (state, action) => {
        state.isLoading = false
      })
      .addCase(refreshEncounters.pending, (state) => {
        state.isLoading = true
      })
      .addCase(refreshEncounters.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(refreshEncounters.fulfilled, (state, action) => {
        state.encounters = action.payload.encounters
        state.pagination = action.payload.pagination
        state.isLoading = false
      })
      .addCase(getFilterOptions.fulfilled, (state, action) => {
        state.encounter_statuses = action.payload.encounter_statuses
        state.note_statuses = action.payload.note_statuses
        if (action.payload.facilities?.length > 0) {
          // if there are no facilities, then we use ALL facilities
          state.facilities = action.payload.facilities?.map((fac) => {
            return {
              ...fac,
              facility_name:
                fac.facility_name +
                getPOSDisplay(state.allFacilities?.find((f) => f?._id === fac?._id)),
            }
          })
        }
        state.procedure_codes = action.payload.procedure_codes
        state.practiceProviders = action.payload.providers
      })
      .addCase(getFacilities.fulfilled, (state, action) => {
        state.facilities = action.payload
        state.allFacilities = action.payload
      })
      .addCase(getPermissions.fulfilled, (state, action) => {
        state.permissions = action.payload
      })
  },
})
export const {
  setEncountersisLoading,
  switchScope,
  setCurrentPage,
  setPageLimit,
  setCurrentFilter,
  clearCurrentFilters,
  setCurrentSearch,
  setCurrentSort,
  setCurrentSortKey,
  setCurrentSortDirection,
  setDashboardInProgressState,
} = encountersSlice.actions

export default encountersSlice.reducer
